/** Pages around accounts: sign-up, invitations, password reset and the forced password change. */
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import type { PublicConfig, User } from '../types';
import { LANGUAGES, getLanguage, setLanguage, t, tError } from '../i18n';

export function AuthBox({ title, subtitle, children, onSubmit }: { title?: string; subtitle?: ReactNode; children: ReactNode; onSubmit?: (e: FormEvent) => void }) {
  const cfg = usePublicConfig();
  const appName = cfg.data?.app_name ?? 'BugTracker';
  return (
    <div className="login-wrap">
      <form className="login-box" onSubmit={onSubmit} noValidate>
        <h1>
          <Link to="/" className="logo" title={appName}>
            {appName.slice(0, 1)}
          </Link>{' '}
          {title ?? appName}
        </h1>
        {subtitle && <p className="muted">{subtitle}</p>}
        {children}
        <LanguagePicker />
      </form>
    </div>
  );
}

/** Small language switch under the login / sign-up forms (before there is an account to save it in). */
export function LanguagePicker() {
  const current = getLanguage();
  return (
    <div className="lang-picker" role="group" aria-label={t('Language')}>
      {LANGUAGES.map((l) => (
        <button
          key={l.code}
          type="button"
          className={`link ${l.code === current ? 'current' : ''}`}
          aria-pressed={l.code === current}
          title={l.label}
          onClick={() => setLanguage(l.code)}
        >
          {l.short}
        </button>
      ))}
    </div>
  );
}

export function usePublicConfig() {
  return useQuery({ queryKey: ['public-config'], queryFn: () => api.get<PublicConfig>('/api/public-config'), staleTime: Infinity });
}

/** After a session starts: go to the return URL, the team picker (several teams) or My View. */
export function useAfterLogin() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return async (res: { user: User | null }, returnTo?: string | null) => {
    qc.clear();
    qc.setQueryData(['session'], res);
    const ret = returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : null;
    if (ret) return navigate(ret, { replace: true });
    if (res.user?.must_change_password) return navigate('/', { replace: true });
    try {
      const t = await api.get<{ teams: unknown[] }>('/api/teams');
      navigate(t.teams.length === 1 ? '/' : '/teams', { replace: true });
    } catch {
      navigate('/', { replace: true });
    }
  };
}

function useForm() {
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return { error, busy, run };
}

// ---------------------------------------------------------------------------
export function SignupPage({ invite }: { invite?: { token: string; email: string; team_name: string } }) {
  const cfg = usePublicConfig();
  const afterLogin = useAfterLogin();
  const [f, setF] = useState({ username: '', email: invite?.email ?? '', password: '', realname: '', team_name: '' });
  const { error, busy, run } = useForm();
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    run(async () => {
      const res = await api.post<{ user: User }>('/api/signup', {
        username: f.username.trim(),
        email: f.email.trim(),
        password: f.password,
        realname: f.realname.trim() || undefined,
        team_name: invite ? undefined : f.team_name.trim() || undefined,
        invite: invite?.token,
        language: getLanguage(),
      });
      await afterLogin(res, invite ? '/' : '/manage/projects');
    });
  };

  if (!invite && cfg.data && !cfg.data.signup_enabled) {
    return (
      <AuthBox subtitle={t('Sign-up is by invitation only. Ask a team administrator to invite you.')}>
        <Link to="/login">{t('Back to login')}</Link>
      </AuthBox>
    );
  }
  return (
    <AuthBox
      title={invite ? t('Join {team}', { team: invite.team_name }) : t('Create your account')}
      subtitle={invite ? t('Create an account to accept the invitation.') : undefined}
      onSubmit={submit}
    >
      {error && <div className="alert error">{tError(error)}</div>}
      <label>
        {t('Username')}
        <input autoFocus value={f.username} onChange={set('username')} autoComplete="username" maxLength={64} />
      </label>
      <label>
        {t('E-mail')}
        <input type="email" value={f.email} onChange={set('email')} autoComplete="email" />
      </label>
      <label>
        <span>
          Your name <span className="subtle">{t('(optional)')}</span>
        </span>
        <input value={f.realname} onChange={set('realname')} autoComplete="name" />
      </label>
      <label>
        <span>
          Password <span className="subtle">{t('(at least 8 characters)')}</span>
        </span>
        <input type="password" value={f.password} onChange={set('password')} autoComplete="new-password" />
      </label>
      {!invite && (
        <label>
          {t('Team name')}
          <input value={f.team_name} onChange={set('team_name')} placeholder={f.username ? t("{name}'s team", { name: f.username }) : t('e.g. Acme')} maxLength={80} />
        </label>
      )}
      <button className="primary" disabled={busy || !f.username || !f.email || f.password.length < 8}>
        {busy ? t('Creating…') : invite ? t('Create account and join') : t('Create account')}
      </button>
      {cfg.data?.legal && (
        <p className="small muted">
          {t('By creating an account you agree to the')} <Link to="/terms">{t('Terms of Service')}</Link> {t('and the')}{' '}
          <Link to="/privacy">{t('Privacy Policy')}</Link>.
        </p>
      )}
      <p className="small muted">
        {t('Already have an account?')} <Link to={invite ? `/login?return=${encodeURIComponent(`/invite/${invite.token}`)}` : '/login'}>{t('Log in')}</Link>
      </p>
    </AuthBox>
  );
}

// ---------------------------------------------------------------------------
export function InvitePage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { error, busy, run } = useForm();
  const { data, error: loadError, isLoading } = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api.get<{ team_name: string; email: string; invited_by: string | null; valid: boolean; logged_in_as: string | null }>(`/api/invites/${token}`),
    retry: false,
  });

  if (isLoading) return <div className="fullpage subtle">{t('Loading…')}</div>;
  if (loadError || !data) {
    return (
      <AuthBox subtitle={tError((loadError as Error)?.message ?? 'This invitation link is not valid.')}>
        <Link to="/login">{t('Go to login')}</Link>
      </AuthBox>
    );
  }
  if (!data.valid) {
    return (
      <AuthBox subtitle={t('This invitation to "{team}" has expired or was already used. Ask for a new one.', { team: data.team_name })}>
        <Link to="/login">{t('Go to login')}</Link>
      </AuthBox>
    );
  }
  if (!data.logged_in_as) return <SignupPage invite={{ token, email: data.email, team_name: data.team_name }} />;

  const accept = () =>
    run(async () => {
      await api.post(`/api/invites/${token}/accept`);
      navigate('/');
      await qc.resetQueries();
    });
  return (
    <AuthBox
      title={t('Join {team}', { team: data.team_name })}
      subtitle={t('{who} invited you to the team "{team}". You\'re logged in as {user}.', {
        who: data.invited_by ?? t('A team administrator'),
        team: data.team_name,
        user: data.logged_in_as,
      })}
      onSubmit={(e) => {
        e.preventDefault();
        accept();
      }}
    >
      {error && <div className="alert error">{tError(error)}</div>}
      <button className="primary" disabled={busy}>
        {busy ? t('Joining…') : t('Join the team')}
      </button>
      <p className="small muted">
        {t('Not you?')}{' '}
        <a
          href="/login"
          onClick={async (e) => {
            e.preventDefault();
            await api.post('/api/logout');
            await qc.resetQueries();
          }}
        >
          {t('Log out')}
        </a>{' '}
        {t('and use another account.')}
      </p>
    </AuthBox>
  );
}

// ---------------------------------------------------------------------------
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const { error, busy, run } = useForm();
  if (sent) {
    return (
      <AuthBox title={t('Check your e-mail')} subtitle={t('If an account uses this address, we sent it a link to choose a new password. The link is valid for 1 hour.')}>
        <Link to="/login">{t('Back to login')}</Link>
      </AuthBox>
    );
  }
  return (
    <AuthBox
      title={t('Reset password')}
      subtitle={t('Enter the e-mail address of your account.')}
      onSubmit={(e) => {
        e.preventDefault();
        run(async () => {
          await api.post('/api/password/forgot', { email: email.trim() });
          setSent(true);
        });
      }}
    >
      {error && <div className="alert error">{tError(error)}</div>}
      <label>
        {t('E-mail')}
        <input autoFocus type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
      </label>
      <button className="primary" disabled={busy || !email.trim()}>
        {busy ? t('Sending…') : t('Send reset link')}
      </button>
      <Link to="/login" className="small">
        {t('Back to login')}
      </Link>
    </AuthBox>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const { error, busy, run } = useForm();
  return (
    <AuthBox
      title={t('Choose a new password')}
      onSubmit={(e) => {
        e.preventDefault();
        run(async () => {
          if (pw1 !== pw2) throw new Error(t('The passwords do not match.'));
          await api.post('/api/password/reset', { token: params.get('token') ?? '', password: pw1 });
          navigate('/login?reset=1', { replace: true });
        });
      }}
    >
      {error && <div className="alert error">{tError(error)}</div>}
      <label>
        <span>
          {t('New Password')} <span className="subtle">{t('(at least 8 characters)')}</span>
        </span>
        <input autoFocus type="password" value={pw1} onChange={(e) => setPw1(e.target.value)} autoComplete="new-password" />
      </label>
      <label>
        {t('Repeat new password')}
        <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" />
      </label>
      <button className="primary" disabled={busy || pw1.length < 8}>
        {busy ? t('Saving…') : t('Set password')}
      </button>
    </AuthBox>
  );
}

/** The link from the confirmation e-mail. */
export function ConfirmEmailPage() {
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const token = params.get('token') ?? '';
  const result = useQuery({
    queryKey: ['confirm-email', token],
    queryFn: () => api.post<{ username: string }>('/api/email/confirm', { token }),
    retry: false,
    staleTime: Infinity,
  });
  useEffect(() => {
    if (result.isSuccess) {
      qc.invalidateQueries({ queryKey: ['session'] });
      qc.invalidateQueries({ queryKey: ['meta'] });
    }
  }, [result.isSuccess, qc]);
  return (
    <AuthBox title={t('Confirm e-mail address')}>
      {result.isPending && <p className="muted">{t('Loading…')}</p>}
      {result.isSuccess && <div className="alert ok">{t('Thank you, your e-mail address is confirmed.')}</div>}
      {result.isError && <div className="alert error">{tError((result.error as Error).message)}</div>}
      {!result.isPending && <Link to="/">{t('Continue')}</Link>}
    </AuthBox>
  );
}

/** Shown instead of the app while the account still has its temporary password. */
export function ChangePasswordPage({ user }: { user: User }) {
  const afterLogin = useAfterLogin();
  const qc = useQueryClient();
  const [current, setCurrent] = useState('');
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const { error, busy, run } = useForm();
  return (
    <AuthBox
      title={t('Choose your password')}
      subtitle={t('Welcome, {name}! Your account was created with a temporary password. Please choose your own before you continue.', { name: user.username })}
      onSubmit={(e) => {
        e.preventDefault();
        run(async () => {
          if (pw1 !== pw2) throw new Error(t('The new passwords do not match.'));
          await api.post('/api/account/password', { current_password: current, new_password: pw1 });
          const res = await api.get<{ user: User }>('/api/session');
          await afterLogin(res);
        });
      }}
    >
      {error && <div className="alert error">{tError(error)}</div>}
      <label>
        {t('Temporary password')}
        <input autoFocus type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
      </label>
      <label>
        <span>
          {t('New Password')} <span className="subtle">{t('(at least 8 characters)')}</span>
        </span>
        <input type="password" value={pw1} onChange={(e) => setPw1(e.target.value)} autoComplete="new-password" />
      </label>
      <label>
        {t('Repeat new password')}
        <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" />
      </label>
      <button className="primary" disabled={busy || !current || pw1.length < 8}>
        {busy ? t('Saving…') : t('Save and continue')}
      </button>
      <a
        href="/login"
        className="small"
        onClick={async (e) => {
          e.preventDefault();
          await api.post('/api/logout');
          qc.clear();
          location.href = '/login';
        }}
      >
        {t('Log out')}
      </a>
    </AuthBox>
  );
}
